import jsQR from "jsqr";

export type VietQrInputMode = "auto" | "upload";

export async function readQrStringFromImageFile(file: File): Promise<string | null> {
  const imageBitmap = await createImageBitmap(file);
  const canvas = document.createElement("canvas");
  canvas.width = imageBitmap.width;
  canvas.height = imageBitmap.height;
  const ctx = canvas.getContext("2d")!;
  ctx.drawImage(imageBitmap, 0, 0);
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const qr = jsQR(imageData.data, canvas.width, canvas.height);
  return qr?.data ?? null;
}

export function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

export function vietQrEmvFromRaw(raw: unknown): string {
  if (!raw || typeof raw !== "object") {
    return "";
  }
  const r = raw as Record<string, unknown>;
  const qs = r.qrString != null ? String(r.qrString).trim() : "";
  const vt = r.vietQrTemplate != null ? String(r.vietQrTemplate).trim() : "";
  return qs || vt;
}

export function truncateVietQrEmvDisplay(s: string, headChars = 28, tailChars = 8): string {
  const t = String(s).trim();
  if (!t) {
    return "";
  }
  const minLen = headChars + tailChars + 1;
  if (t.length <= minLen) {
    return t;
  }
  return `${t.slice(0, headChars)}…${t.slice(-tailChars)}`;
}

export function normalizeVietQrRaw(
  raw: Record<string, unknown> | null | undefined
): Record<string, unknown> {
  const o = raw && typeof raw === "object" ? {...raw} : {};
  const qs = o.qrString != null ? String(o.qrString).trim() : "";
  const vt = o.vietQrTemplate != null ? String(o.vietQrTemplate).trim() : "";
  if (!qs && vt) {
    o.qrString = vt;
  }
  return o;
}

export function buildVietQrRawPayload(raw: Record<string, unknown> | null | undefined): Record<string, string> {
  const o: Record<string, string> = {};
  const emv =
    (raw?.qrString != null ? String(raw.qrString).trim() : "") ||
    (raw?.vietQrTemplate != null ? String(raw.vietQrTemplate).trim() : "");
  if (emv) {
    o.qrString = emv;
    o.vietQrTemplate = emv;
  }
  if (raw?.qrImage != null && String(raw.qrImage).trim()) {
    o.qrImage = String(raw.qrImage);
  }
  return o;
}

export function isVietQrRawComplete(raw: Record<string, unknown> | null | undefined): boolean {
  const qs = raw?.qrString != null ? String(raw.qrString).trim() : "";
  const vt = raw?.vietQrTemplate != null ? String(raw.vietQrTemplate).trim() : "";
  const qi = raw?.qrImage != null ? String(raw.qrImage).trim() : "";
  return !!(qs || vt || qi);
}

export function clearVietQrRawFields(raw: Record<string, unknown> | null | undefined): void {
  if (!raw || typeof raw !== "object") {
    return;
  }
  delete raw.qrString;
  delete raw.qrImage;
  delete raw.vietQrTemplate;
}

export function detectVietQrInputMode(raw: Record<string, unknown> | null | undefined): VietQrInputMode {
  return isVietQrRawComplete(raw) ? "upload" : "auto";
}

export function isVietQrAccountNumberValid(accountNumber: string | null | undefined): boolean {
  const value = accountNumber != null ? String(accountNumber).trim() : "";
  return !!value && /^\d+$/.test(value);
}

export function qrisEmvFromRaw(raw: unknown): string {
  if (!raw || typeof raw !== "object") {
    return "";
  }
  const r = raw as Record<string, unknown>;
  const qs = r.qrString != null ? String(r.qrString).trim() : "";
  const qt = r.qrisTemplate != null ? String(r.qrisTemplate).trim() : "";
  return qs || qt;
}

export function isQrisRawComplete(raw: Record<string, unknown> | null | undefined): boolean {
  const qs = raw?.qrString != null ? String(raw.qrString).trim() : "";
  const qt = raw?.qrisTemplate != null ? String(raw.qrisTemplate).trim() : "";
  const qi = raw?.qrImage != null ? String(raw.qrImage).trim() : "";
  return !!(qs || qt || qi);
}

export function buildQrisRawPayload(raw: Record<string, unknown> | null | undefined): Record<string, string> {
  const o: Record<string, string> = {};
  const emv = raw?.qrString != null ? String(raw.qrString).trim() : "";
  if (emv) {
    o.qrString = emv;
  }
  if (raw?.qrImage != null && String(raw.qrImage).trim()) {
    o.qrImage = String(raw.qrImage);
  }
  return o;
}

export function clearQrisRawFields(raw: Record<string, unknown> | null | undefined): void {
  if (!raw || typeof raw !== "object") {
    return;
  }
  delete raw.qrString;
  delete raw.qrImage;
  delete raw.qrisTemplate;
}

export function qrisBackendErrorLabel(message: string | null | undefined): string | null {
  const m = (message ?? "").toLowerCase();
  if (m.includes("qris required")) {
    return "labels.qris_required";
  }
  if (m.includes("invalid qris")) {
    return "labels.qris_invalid";
  }
  if (m.includes("qris image unreadable")) {
    return "labels.qris_image_unreadable";
  }
  return null;
}

export function pixEmvFromRaw(raw: unknown): string {
  if (!raw || typeof raw !== "object") {
    return "";
  }
  const r = raw as Record<string, unknown>;
  const qs = r.qrString != null ? String(r.qrString).trim() : "";
  const pt = r.pixTemplate != null ? String(r.pixTemplate).trim() : "";
  return qs || pt;
}

export function isPixRawComplete(raw: Record<string, unknown> | null | undefined): boolean {
  const qs = raw?.qrString != null ? String(raw.qrString).trim() : "";
  const pt = raw?.pixTemplate != null ? String(raw.pixTemplate).trim() : "";
  const qi = raw?.qrImage != null ? String(raw.qrImage).trim() : "";
  return !!(qs || pt || qi);
}

export function buildPixRawPayload(raw: Record<string, unknown> | null | undefined): Record<string, string> {
  const o: Record<string, string> = {};
  const emv = raw?.qrString != null ? String(raw.qrString).trim() : "";
  if (emv) {
    o.qrString = emv;
  }
  if (raw?.qrImage != null && String(raw.qrImage).trim()) {
    o.qrImage = String(raw.qrImage);
  }
  return o;
}

export function clearPixRawFields(raw: Record<string, unknown> | null | undefined): void {
  if (!raw || typeof raw !== "object") {
    return;
  }
  delete raw.qrString;
  delete raw.qrImage;
  delete raw.pixTemplate;
}

export function pixBackendErrorLabel(message: string | null | undefined): string | null {
  const m = (message ?? "").toLowerCase();
  if (m.includes("pix required")) {
    return "labels.pix_required";
  }
  if (m.includes("invalid pix")) {
    return "labels.pix_invalid";
  }
  if (m.includes("pix image unreadable")) {
    return "labels.pix_image_unreadable";
  }
  return null;
}
